// Copyright (c) 2020 WiseTime. All rights reserved.

import React, { useContext, useState } from "react"
import styled, {
  css,
  ThemeContext,
  SimpleInterpolation,
} from "styled-components/macro"
import { darken } from "polished"
import { useClickOutside } from "./useClickOutside"
import { TextEllipsis, SortDropdownWrapper } from "./styles"
import { DropdownWrapper } from "./Dialog"
import { useWindowSize } from "../../app/useWindowSize"
import { Option as OptionType } from "../../lib/utils"

const Wrapper = styled.div<{ type: DropdownType }>`
  cursor: pointer;
  display: inline-block;
  vertical-align: top;
  position: relative;
  white-space: nowrap;
  color: ${({ theme }): string => theme.colors.mortar};
  line-height: 28px;
  border: 1px solid ${({ theme }): string => theme.colors.gainsboro};
  border-radius: 2px;
  
  ${SortDropdownWrapper} & {
    height: 14px;
    line-height: 15px;
    box-shadow: none;
    border: none;
  }
  
  ${DropdownWrapper} & {
    width: 100%;
    height: 40px;
    line-height: 38px; 
    box-shadow: none;
  }
  
  ${({ type }): SimpleInterpolation => type === "primary" && css`
    width: 100%;
  `}
`

const DropdownHeader = styled.span<{ type: DropdownType }>`
  display: flex;
  align-items: center;
  cursor: pointer;
  user-select: none;
  position: relative;
  padding: 0 .7rem;
  background-color: transparent;
  transition: background-color .3s ease;
  &:hover {
    background-color: ${({ theme }): string => theme.colors.gainsboro};
  
    [data-tooltip] {
      display: inline-block;
    }
  }
  
  ${SortDropdownWrapper} & {
    &:hover {
      background-color: transparent;
    }
  }
  
  ${({ type, theme }): SimpleInterpolation => type === "primary" && css`
    background-color: ${theme.colors.mercury};
    &:hover {
      background-color: ${theme.colors.gainsboro};
    }
  `}
`

const DropdownLabel = styled.span`
  ${TextEllipsis};
  margin-right: .5rem;
`

export const DropdownSelected = styled.span<{ isActive: boolean, type: DropdownType }>`
  ${TextEllipsis};
  display: block;
  font-weight: 600;
  position: relative;
  color: ${({ theme }): string => theme.colors.mortar};
  
  ${SortDropdownWrapper} & {
    color: ${({ theme }): string => theme.colors.indigo};
    transition: color .3s ease;
    &:hover {
      color: ${({ theme }): string => darken(0.1, theme.colors.indigo)};
    }
  }
  
  ${DropdownWrapper} & {
    width: 100%; 
    padding: 0 3rem 0 0;
  }
  
  ${({ type, theme }): SimpleInterpolation => type === "primary" && css`
    color: ${theme.colors.mortar};
  `}
`

export const ArrowIcon = styled.span<{ isActive: boolean, isVisible: boolean, type: DropdownType }>`
  display: ${({ isVisible }): string => isVisible ? "inline-block" : "none"};
  border-top: 4px solid ${({ theme }): string => theme.colors.mortar};
  border-left: 4px solid transparent;
  border-right: 4px solid transparent;
  margin-left: 7px;
  ${({ isActive }): SimpleInterpolation => isActive ? css`
    transform: rotate(180deg);
  ` : ""}
  
  ${SortDropdownWrapper} & {
    border-top-color: ${({ theme }): string => theme.colors.indigo};
  }
  
  ${({ type, theme }): SimpleInterpolation => type === "primary" && css`
    border-top-color: ${theme.colors.mortar};
  `}
`

const DropdownContent = styled.div<{ isActive: boolean, type: DropdownType }>`
  position: absolute;
  left: 0;
  bottom: -1px;
  z-index: 3;
  transform: translateY(100%);
  min-width: 100%;
  background-color: ${({ theme }): string => theme.colors.white};
  border-width: 0 1px 1px 1px;
  border-style: solid;
  border-color: ${({ theme }): string => theme.colors.mercury};
  border-radius: 0 0 3px 3px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, .15);
  flex-direction: column;
  display: ${({ isActive }): string => isActive ? "flex" : "none"};
  
  ${({ type }): SimpleInterpolation => type === "primary" && css`width: 100%;`}
`

const DropdownList = styled.ul`
  list-style: none;
  margin: 0;
  color: ${({ theme }): string => theme.colors.dimGray};
`

const DropdownListOption = styled.div<{ isSelected: boolean }>`
  ${TextEllipsis};
  display: block;
  font-weight: 600;
  height: 32px;
  line-height: 32px;
  padding: 0 1.6rem 0 .6rem;
  background-color: ${({ isSelected, theme }): string => isSelected ? theme.colors.lavender : ""};
  transition: background-color .3s;
  
  &:hover {
    background-color: ${({ theme }): string => theme.colors.mercury};
  }
`

export type Option = OptionType & {
  prefixNode?: React.ReactNode,
}

type DropdownType = "primary" | undefined

type Props = {
  isCollapsedSidebar?: boolean,
  selectedOption: Option["value"],
  options: Option[],
  onChange: (value: Option["value"]) => void,
  className?: string,
  type?: DropdownType,
  title?: string,
  label?: string,
  tooltip?: React.ReactNode,
  divider?: boolean,
}

const Dropdown = ({
  isCollapsedSidebar,
  selectedOption,
  options,
  onChange,
  className,
  type = undefined,
  title,
  label,
  tooltip = null,
}: Props): React.ReactElement<Props> => {
  const { width } = useWindowSize()
  const theme = useContext(ThemeContext)
  const isMobile = width < (isCollapsedSidebar ? theme.screen.lgNumericToolbar : theme.screen.xlgNumericToolbar) && label !== undefined
  const [isActive, setIsActive] = useState(false)
  const onClick = (value: Option["value"]): void => {
    onChange(value)
    setIsActive(false)
  }
  const dropdownRef = React.useRef(null)
  useClickOutside(
    dropdownRef.current,
    isActive,
    ():void => setIsActive(false),
  )
  const headerSelectedOption: Option | undefined = options.find(option => option.value === selectedOption)
  return (
    <Wrapper
      ref={dropdownRef}
      className={className}
      type={type}
      title={title}
    >
      <DropdownHeader onClick={(): void => setIsActive(!isActive)} type={type}>
        {label && <DropdownLabel>{label}</DropdownLabel>}
        <DropdownSelected
          isActive={isActive}
          type={type}
          hidden={isMobile}
        >
          {headerSelectedOption?.prefixNode}
          {headerSelectedOption?.label}
        </DropdownSelected>
        <ArrowIcon type={type} isActive={isActive} isVisible={options.length > 1}/>
        {tooltip}
      </DropdownHeader>
      <DropdownContent type={type} isActive={isActive}>
        <DropdownList>
          {options.map(({ value, label, prefixNode }: Option) => (
            <DropdownListOption
              key={value}
              isSelected={value === selectedOption}
              hidden={value === selectedOption && !isMobile}
              onClick={(): void => onClick(value)}
            >
              {prefixNode}
              {label}
            </DropdownListOption>
          ))}
        </DropdownList>
      </DropdownContent>
    </Wrapper>
  )
}

export default Dropdown
